import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { FlyIn } from '@ff-os/shared-react/components/fly-in/FlyIn';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from 'classnames';
import { selectActiveVariant, selectCanRemoveGroup } from '../../../_redux-groups';
import { applyMagicBorderThunk } from '../../../thunks/applyMagicBorderThunk';
import { removeGroup } from '../../../thunks/removeGroup';
import { updateVariant } from '../../../thunks/updateVariant';
import styles from './PhotoPrintGroupMobileOverlay.module.scss';

const _PhotoPrintGroupMobileOverlay = ({
    isOpen, setOpen, canRemove, remove, openEditWindow, isAccessory,
    applyMagicBorder, updateVariant, activeVariant, closeGroup
}: Props) => {

    const { t } = useTranslation();

    const hasBorder = activeVariant.borderId !== "without-border";
    const hasPhotoFunBorder = activeVariant.borderId === "fotofun-border";

    const editClassName = classNames(styles.editButton, {
        'hidden': isAccessory
    });

    const removeBorderClassName = classNames(styles.removeBorderButton, {
        [styles.disabled]: !hasBorder,
        'hidden': !hasPhotoFunBorder
    });

    const deleteGroupClassName = classNames(styles.deleteButton, {
        'hidden': !canRemove
    });

    const editWindow = () => {
        openEditWindow();
        setOpen(false);
    };

    const removeMagicBorder = () => {
        if (hasBorder) {
            applyMagicBorder(null);
            updateVariant(activeVariant.paperId, activeVariant.sizeId, "without-border");
            setOpen(false);
        }
    };

    const removeGroupAndCloseWindow = () => {
        closeGroup();
        // Add little timeout to close all groupwindows before removing the group
        setTimeout(() => remove());
    };

    return (
        <>
            {isOpen && (
                <WindowStackItem>
                    <FlyIn from='bottom' hide={() => setOpen(false)}>

                        <FlyIn.MenuItem
                            icon={<Icon type='general-ui-pencil' />}
                            onClick={editWindow}
                            className={editClassName}>
                            {t('image_uploader.change_product')}
                        </FlyIn.MenuItem>

                        <FlyIn.MenuItem
                            icon={<Icon type='general-ui-eraser' />}
                            onClick={removeMagicBorder}
                            className={removeBorderClassName}>
                            {t('photo_prints.remove_magic_frame')}
                        </FlyIn.MenuItem>

                        <FlyIn.MenuItem
                            icon={<Icon className={styles.deleteIcon} type='general-ui-bin' />}
                            className={deleteGroupClassName}
                            onClick={removeGroupAndCloseWindow}>
                            {t('photo_prints.remove_group')}
                        </FlyIn.MenuItem>

                    </FlyIn>
                </WindowStackItem>
            )}
        </>
    );
};

type OwnProps = {
    groupId: string;
    isOpen: boolean;
    isAccessory: boolean;
    setOpen: (value: boolean) => void;
    openEditWindow: () => void;
    closeGroup: () => void;
};

type Props = OwnProps & {
    canRemove: boolean;
    remove: () => void;
    applyMagicBorder: (magicBorderId: string| null) => void;
    updateVariant: (paperId: string | undefined, sizeId: string | undefined, borderId: string | undefined) => void;
    activeVariant: PhotoPrintsJson.Variant;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    canRemove: selectCanRemoveGroup(state, ownProps.groupId),
    activeVariant: selectActiveVariant(state, ownProps.groupId)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    remove: () => dispatch(removeGroup(ownProps.groupId)),
    applyMagicBorder: (magicBorderId: string | null) => {
        dispatch(applyMagicBorderThunk(ownProps.groupId, magicBorderId));
    },
    updateVariant: (paperId: string | undefined, sizeId: string | undefined, borderId: string | undefined) => {
        dispatch(updateVariant(paperId, sizeId, ownProps.groupId, borderId));
    }
});

export const PhotoPrintGroupMobileOverlay = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintGroupMobileOverlay);